/**
 * AI Review Service client — API methods for triggering AI contract review,
 * listing suggestions, and accepting/dismissing suggestions.
 *
 * Requirements: 7.3, 7.4, 7.5
 */

import { apiClient } from '@/services/apiClient';
import type { RedlineSuggestion, PlaybookRule } from '@/types';

/** Result returned by the trigger review endpoint. */
export interface AIReviewResult {
  contractId: number;
  suggestions: RedlineSuggestion[];
  disclaimer: string;
}

/** Result returned by the list suggestions endpoint. */
export interface SuggestionsResult {
  suggestions: RedlineSuggestion[];
  disclaimer: string;
}

/** A suggestion enriched with playbook rule metadata for display. */
export interface EnrichedSuggestion extends RedlineSuggestion {
  ruleCategory: string;
  ruleName: string;
}

/** Suggestions grouped by playbook category. */
export interface GroupedSuggestions {
  category: string;
  suggestions: EnrichedSuggestion[];
}

export const aiReviewService = {
  /**
   * Trigger an AI review for a contract.
   * POST /api/contracts/:id/ai-review
   */
  triggerReview(contractId: number): Promise<AIReviewResult> {
    return apiClient.post(`contracts/${contractId}/ai-review`);
  },

  /**
   * List all suggestions for a contract.
   * GET /api/contracts/:id/ai-review/suggestions
   */
  listSuggestions(contractId: number): Promise<SuggestionsResult> {
    return apiClient.get(`contracts/${contractId}/ai-review/suggestions`);
  },

  /**
   * Accept a suggestion.
   * PUT /api/ai-review/suggestions/:id/accept
   */
  acceptSuggestion(suggestionId: number): Promise<RedlineSuggestion> {
    return apiClient.put(`ai-review/suggestions/${suggestionId}/accept`);
  },

  /**
   * Dismiss a suggestion.
   * PUT /api/ai-review/suggestions/:id/dismiss
   */
  dismissSuggestion(suggestionId: number): Promise<RedlineSuggestion> {
    return apiClient.put(`ai-review/suggestions/${suggestionId}/dismiss`);
  },
};

/**
 * Enrich suggestions with playbook rule metadata (category, ruleName)
 * and group them by category, sorted by risk rating descending within
 * each group.
 */
export function groupSuggestionsByCategory(
  suggestions: RedlineSuggestion[],
  rules: PlaybookRule[],
): GroupedSuggestions[] {
  const ruleMap = new Map<number, PlaybookRule>();
  for (const rule of rules) {
    ruleMap.set(rule.ruleId, rule);
  }

  const enriched: EnrichedSuggestion[] = suggestions.map((s) => {
    const rule = ruleMap.get(s.ruleId);
    return {
      ...s,
      ruleCategory: rule?.category ?? 'Uncategorized',
      ruleName: rule?.ruleName ?? `Rule #${s.ruleId}`,
    };
  });

  // Group by category
  const groups = new Map<string, EnrichedSuggestion[]>();
  for (const s of enriched) {
    const existing = groups.get(s.ruleCategory) ?? [];
    existing.push(s);
    groups.set(s.ruleCategory, existing);
  }

  // Sort suggestions within each group by risk rating descending
  const riskOrder: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

  const result: GroupedSuggestions[] = [];
  for (const [category, items] of groups) {
    items.sort((a, b) => (riskOrder[a.riskRating] ?? 3) - (riskOrder[b.riskRating] ?? 3));
    result.push({ category, suggestions: items });
  }

  // Sort groups: categories with highest-risk suggestions first
  result.sort((a, b) => {
    const aMax = riskOrder[a.suggestions[0]?.riskRating] ?? 3;
    const bMax = riskOrder[b.suggestions[0]?.riskRating] ?? 3;
    return aMax - bMax;
  });

  return result;
}
